import isNil from 'lodash/isNil';

import { FEATURES } from '#client/manifest';

const test = 'zenFeatureDetection';

const availableCache = [];
// Used to stored values that need to persist across sessions and storage cleans

export const persistedLocalStorageKeys = {
  ZENBUSINESS_PARTNER: 'zenbusiness_partner',
  ZENBUSINESS_PARTNER_TIMER: 'zenbusiness_partner_timer',
  ZENBUSINESS_ENTRY_POINT_GROUP: 'zenbusiness_entry_point_group',
  // Tracking keys
  UC_ACCEPTED_ALL: 'uc_accepted_all',
  UC_ACCEPTED_ALL_FUNCTIONAL: 'uc_accepted_all_functional',
  UC_ACCEPTED_ALL_MARKETING: 'uc_accepted_all_marketing'
};

/**
 * Determine if web storage is available in the browser
 * As default we use localStorage, but sessionStorage is also available for auth0 data
 *
 * @param {string} sType `local` | `session`
 * @returns {null|boolean}
 */
export function available(sType = 'local') {
  if (availableCache.includes(sType)) {
    return true;
  }
  try {
    window[`${sType}Storage`].setItem(test, test);
    window[`${sType}Storage`].removeItem(test);
    availableCache.push(sType);
    return true;
  } catch (e) {
    return false;
  }
}

/**
 * Wrapper for getItem method from HTML5 Web Storage API
 * As default we use localStorage, but sessionStorage is also available for auth0 data
 * Returns null if value not available
 *
 * @param {string} key
 * @param {string} sType `local` | `session`
 * @returns {string|null}
 */
function getItem(key, sType = 'local') {
  if (available(sType)) {
    return window[`${sType}Storage`].getItem(key);
  }
  return null;
}

/**
 * Wrapper for setItem method from HTML5 Web Storage API
 * As default we use localStorage, but sessionStorage is also available for auth0 data
 * Returns null if functionality is not available on users browser
 *
 * @param {string} key string
 * @param {string} val string
 * @param {string} sType `local` | `session`
 * @returns {null|void}
 */
function setItem(key, val, sType = 'local') {
  if (available(sType) && !isNil(key) && !isNil(val)) {
    return window[`${sType}Storage`].setItem(key, val);
  }
  return null;
}

/**
 * Wraps for removeItem method from HTML5 Web Storage API
 * As default we use localStorage, but sessionStorage is also available for auth0 data
 * Returns null when functionality is not available on user's browser
 *
 * @param {string} key
 * @param {string} sType `local` | `session`
 * @returns {null|void}
 */
function removeItem(key, sType = 'local') {
  if (available(sType)) {
    return window[`${sType}Storage`].removeItem(key);
  }
  return null;
}

/**
 * Helper function to define and manage local or session storage access
 *
 * @typedef {function(any): any} Setter
 * @typedef {function(): any} Getter
 *
 * @param {object} options
 * @param {string} options.key
 * @param {boolean} options.isJson - stringify/parse value
 * @param {'local'|'session'} options.sType
 * @returns {{
 *  set: Setter;
 *  get: Getter;
 *  remove: Function;
 * }}
 */
export const storageOps = (options = {}) => {
  const {
    key = '__unknown__',
    isJson = false,
    sType = 'local'
  } = options;
  return {
    set: (value) => {
      let valueToWrite = value;
      if (isJson) {
        try {
          valueToWrite = JSON.stringify(value);
        } catch (err) {
        // eslint-disable-next-line no-console
          console.error(`Unable to write JSON to ${sType} Storage API for`, key);
        }
      }
      setItem(key, valueToWrite, sType);
      return value;
    },
    get: () => {
      let valueToReturn = getItem(key, sType);
      if (isJson) {
        try {
        // null can be JSON.parse()'d
          if (isNil(valueToReturn)) {
            valueToReturn = {};
          } else {
            valueToReturn = JSON.parse(valueToReturn);
          }
        } catch (err) {
        // eslint-disable-next-line no-console
          console.error(`Unable to parse ${sType} Storage API value for`, key);
        }
      }
      return valueToReturn;
    },
    remove: () => removeItem(key, sType)
  };
};

export function clear() {
  if (available()) {
    // Create a dict of keys/vals to be restored after clear
    const restoreVals = Object.values(persistedLocalStorageKeys).reduce((accum, curPKey) => {
      const storedVal = getItem(curPKey);
      if (storedVal) {
        return { ...accum, [curPKey]: storedVal };
      }
      return accum;
    }, {});
    if (FEATURES.sessionStorageEnabled) {
      window.sessionStorage.clear();
    }
    window.localStorage.clear();
    Object.entries(restoreVals).forEach(([rKey, rVal]) => {
      setItem(rKey, rVal);
    });
  }
  return null;
}
